import type { TableExport, TablePrint } from "@api/generated/model";
import { getWorkspaceTableExportUrl, getWorkspaceTablePrintUrl } from "@api/generated/workspace/workspace";
import { apiDownloadJson, apiShowJson, openTab } from "@api/http";
import { t } from "@i18n";
import { notifications } from "@mantine/notifications";
import { cellText, formatNumber, fullTime, isoDate, localToday } from "@ui/format";
import { blank, type CellValue } from "./model";
import { columnName, type DataTableModel } from "./useDataTable";

type SheetCell = TableExport["rows"][number][number];

/** What a table goes to: an Excel file (cells as values) or pages to print (cells as the page shows them). */
export type FileKind = "excel" | "print";

/** A table as the person sees it, whatever draws it (a ``DataTable``, the trial data grid): its columns in their
 *  order, its rows (every page) with each cell as the file needs it, and its settings in words. */
export type TableView = {
  columns: { name: string; numeric?: boolean; date?: boolean; width?: number }[];
  rows: SheetCell[][];
  settings: string[];
};

/** Where a table was taken from: the page (its heading), the table's own name (``title``, else the dialog's) and
 *  what it belongs to (``details``: the page's top part in words, "Sender: …"). */
export type TablePlace = { page: string; title: string; details?: string[] };

export function tablePlace(el: Element, title?: string): TablePlace {
  const page = document.querySelector("h1")?.textContent?.trim() ?? "";
  const dialog = el.closest("[role=dialog]")?.querySelector("h2")?.textContent?.trim() ?? "";
  return { page, title: title || dialog };
}

// a dragged width (px) as Excel's (characters), at most Excel's widest column
const PX_PER_CHARACTER = 7;
const MAX_WIDTH = 255;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** The most rows a printout takes (the server's limit: hundreds of pages). */
export const PRINT_ROWS_MAX = 20_000;

/** A cell for Excel: empty stays empty, a number in a number column a number (Excel adds them up), a day in a date
 *  column a day (ISO), else the cell as the page shows it (``shown``: a label, a code, a formatted date) or its value
 *  as text (yes / no as read). */
export function sheetCell(value: unknown, column: { numeric?: boolean; date?: boolean; shown?: () => string }): SheetCell {
  if (blank(value as CellValue)) return null;
  if (column.date && (value instanceof Date || typeof value === "string")) return value instanceof Date ? isoDate(value) : value;
  if (column.numeric || !column.shown) {
    const n = typeof value === "number" ? value : column.numeric ? Number(value) : Number.NaN;
    if (Number.isFinite(n)) return n;
  }
  if (column.shown) return column.shown();
  return typeof value === "boolean" ? cellText(value) : String(value);
}

/** A column's name in a file: under its group's heading (a generation, a repeat) when there is one. */
export const sheetColumnName = (name: string, group?: unknown) => (typeof group === "string" && group ? `${group} ${name}` : name);

/** A table's settings in words, the same for every kind of table: "Sorted by: Count (descending)", "Filters: …",
 *  "Hidden columns: …", "12 of 120 rows shown" (only the rows loaded, when the page has more). */
export function settingsLines(s: {
  sort: { name: string; descending: boolean }[];
  filters: string[];
  hidden?: string[];
  shown: number;
  total: number;
  partial?: boolean;
}): string[] {
  const keys = s.sort.map((k) => `${k.name} (${t(k.descending ? "dataTable.descending" : "dataTable.ascending")})`);
  const rows =
    s.shown === s.total
      ? t("dataTable.rowCount", { count: formatNumber(s.total) })
      : t("dataTable.shown", { shown: formatNumber(s.shown), total: formatNumber(s.total) });
  return [
    keys.length ? `${t("dataTable.sortedBy")}: ${keys.join(", ")}` : "",
    s.filters.length ? t("dataTable.printFilters", { filters: s.filters.join("; ") }) : "",
    s.hidden?.length ? t("dataTable.hiddenNote", { names: s.hidden.join(", ") }) : "",
    s.partial ? `${rows} · ${t("dataTable.loadedOnly", { count: s.total })}` : rows,
  ].filter(Boolean);
}

/** A ``DataTable`` as shown, for ``kind``: the columns with something to write (not a tick box or the row's buttons),
 *  the rows the filters let through in the sort's order (every page), and its settings in words. A column is a date
 *  column when its first value is a day. */
export function dataTableView<T>(table: DataTableModel<T>, kind: FileKind): TableView {
  const columns = table.columns
    .filter((c) => c.value || c.text)
    .map((c) => {
      const { value, text } = c;
      const first = value && table.rows.find((r) => !blank(value(r)));
      const day = first !== undefined && value ? value(first) : undefined;
      const date = day instanceof Date || (typeof day === "string" && ISO_DAY.test(day));
      const px = table.widths[c.id];
      const width = px ? Math.min(Math.round(px / PX_PER_CHARACTER), MAX_WIDTH) : undefined;
      // as the page shows it (print; empty: null), or as Excel takes it
      const shown = (row: T): string | null => {
        const v = value?.(row);
        return value && blank(v) ? null : (text?.(row) ?? cellText(v)) || null;
      };
      const cell =
        kind === "print"
          ? shown
          : (row: T): SheetCell =>
              value ? sheetCell(value(row), { numeric: c.numeric, date, shown: text && (() => text(row)) }) : text?.(row) || null;
      return { head: { name: sheetColumnName(columnName(c), c.group?.header), numeric: c.numeric, date, width }, cell };
    });
  const names = new Map(table.columns.map((c) => [c.id, columnName(c)]));
  const filters = Object.entries(table.filters).flatMap(([id, text]) => (names.has(id) ? [`${names.get(id)}: “${text.trim()}”`] : []));
  return {
    columns: columns.map((c) => c.head),
    rows: table.rows.map((row) => columns.map((c) => c.cell(row))),
    settings: settingsLines({
      sort: table.sort.map((k) => ({ name: names.get(k.field) ?? k.field, descending: k.descending })),
      filters: table.filtered ? filters : [],
      hidden: table.hidden.map(columnName),
      shown: table.rows.length,
      total: table.total,
      partial: table.partial,
    }),
  };
}

/** After the browser has drawn what changed (a button showing it is busy): a long table takes a moment to read. */
const afterPaint = () => new Promise<void>((done) => requestAnimationFrame(() => setTimeout(done)));

/** What the table belongs to, where, when and how it was taken: on top of the file or the first page. */
const taken = (place: TablePlace, view: TableView) => ({
  ...place,
  day: localToday(),
  printed: fullTime(new Date().toISOString()),
  settings: view.settings,
});

/** The view of ``kind`` "excel" (read once the button shows it is busy) as an .xlsx file named after the page, the
 *  table and the day (the server names it), with when and where it was taken and its settings on top. */
export async function downloadExcel(viewOf: (kind: FileKind) => TableView | null, place: TablePlace): Promise<void> {
  await afterPaint();
  const view = viewOf("excel");
  if (!view) return;
  const body: TableExport = { ...taken(place, view), columns: view.columns, rows: view.rows };
  return apiDownloadJson(getWorkspaceTableExportUrl(), body, "BREEDER.xlsx");
}

/** The view of ``kind`` "print" as A4 pages to print, opened in a new tab (called at the click: the tab opens at
 *  once): when and where it was printed, its settings, page numbers. ``rows``: how many, to refuse a printout of too
 *  many before anything is made. */
export async function openPrint(viewOf: (kind: FileKind) => TableView | null, place: TablePlace, rows: number): Promise<void> {
  if (rows > PRINT_ROWS_MAX) {
    notifications.show({
      color: "red",
      message: t("dataTable.printTooMany", { count: formatNumber(rows), max: formatNumber(PRINT_ROWS_MAX) }),
    });
    return;
  }
  const tab = openTab(t("dataTable.printWaiting"));
  await afterPaint();
  const view = viewOf("print");
  if (!view) return void tab?.close();
  const body: TablePrint = {
    ...taken(place, view),
    columns: view.columns.map(({ name, numeric }) => ({ name, numeric })),
    rows: view.rows as TablePrint["rows"], // the print view's cells are texts
  };
  return apiShowJson(tab, getWorkspaceTablePrintUrl(), body, "BREEDER.pdf");
}
